import React, { useState, useEffect } from 'react'
import { Box, TextField, Button, Grid, Typography, Tooltip, FormControl, Select, MenuItem } from '@mui/material';
import { useDispatch } from 'react-redux';
import coreUtils from '../../utils/coreUtils';
import CustomButton from '../Common/CustomButton';
import { Close, Add, InfoOutlined } from '@mui/icons-material'
import LoadingOverlay from '../Common/LoadingOverlay';
import * as monaco from 'monaco-editor';

import { generateRuleWithAI, createTestRule } from '../../redux-store/testRulesReducers/testRulesActions';
import { useSnackbar } from "../../contexts/CustomSnackbarContext";
import MonacoEditorWithFullscreen from 'components/Common/MonacoEditorWithFullscreen';
import { useTheme } from 'contexts/ThemeContext';
import { Database, FileCode } from 'lucide-react';

// Define custom themes for JavaScript
monaco.editor.defineTheme("js-theme-light", {
  base: 'vs',
  inherit: true,
  rules: [
    { token: 'keyword', foreground: '0000FF' },      // Blue for keywords
    { token: 'string', foreground: 'A31515' },       // Red for strings
    { token: 'number', foreground: '098658' },       // Green for numbers
    { token: 'comment', foreground: '008000' },      // Green for comments
    { token: 'function', foreground: '795E26' },     // Brown for functions
    { token: 'variable', foreground: '001080' },     // Dark blue for variables
    { token: 'operator', foreground: '000000' },     // Black for operators
    { token: 'delimiter', foreground: '000000' },    // Black for delimiters
    { token: 'bracket', foreground: '000000' },      // Black for brackets
  ],
  colors: {
    'editor.background': '#ffffff',
    'editor.foreground': '#000000',
    'editorLineNumber.foreground': '#2B91AF',
    'editor.lineHighlightBackground': '#f0f0f0',
    'editor.lineHighlightBorder': '#f0f0f0',
    'editorGutter.background': '#f3f3f3',
    'editor.selectionBackground': '#add6ff',
    'editor.inactiveSelectionBackground': '#e5ebf1',
    'editorCursor.foreground': '#000000',
    'editorWhitespace.foreground': '#A0A0A0',
    'editorIndentGuide.background': '#D3D3D3',
    'editorIndentGuide.activeBackground': '#939393',
    'editorLineNumber.activeForeground': '#000000',
    'editor.selectionHighlightBackground': '#add6ff',
    'editor.wordHighlightBackground': '#575757',
    'editor.wordHighlightStrongBackground': '#004972',
  }
});

monaco.editor.defineTheme("js-theme-dark", {
  base: 'vs-dark',
  inherit: true,
  rules: [
    { token: 'keyword', foreground: '569CD6' },      // Light blue for keywords
    { token: 'string', foreground: 'CE9178' },       // Light red for strings
    { token: 'number', foreground: 'B5CEA8' },       // Light green for numbers
    { token: 'comment', foreground: '6A9955' },      // Light green for comments
    { token: 'function', foreground: 'DCDCAA' },     // Light yellow for functions
    { token: 'variable', foreground: '9CDCFE' },     // Light blue for variables
    { token: 'operator', foreground: 'D4D4D4' },     // Light gray for operators
    { token: 'delimiter', foreground: 'D4D4D4' },    // Light gray for delimiters
    { token: 'bracket', foreground: 'D4D4D4' },      // Light gray for brackets
  ],
  colors: {
    'editor.background': '#1E1E1E',
    'editor.foreground': '#D4D4D4',
    'editorLineNumber.foreground': '#858585',
    'editor.lineHighlightBackground': '#2A2D2E',
    'editor.lineHighlightBorder': '#2A2D2E',
    'editorGutter.background': '#1E1E1E',
    'editor.selectionBackground': '#264F78',
    'editor.inactiveSelectionBackground': '#3A3D41',
    'editorCursor.foreground': '#FFFFFF',
    'editorWhitespace.foreground': '#404040',
    'editorIndentGuide.background': '#404040',
    'editorIndentGuide.activeBackground': '#707070',
    'editorLineNumber.activeForeground': '#FFFFFF',
    'editor.selectionHighlightBackground': '#264F78',
    'editor.wordHighlightBackground': '#575757',
    'editor.wordHighlightStrongBackground': '#004972',
  }
});

const TestRuleCustom = ({ onSave, testCaseSetId, onCancel }) => {
  const dispatch = useDispatch();
  const { openSnackbar } = useSnackbar();
  const [ruleTitle, setRuleTitle] = useState("");
  const [ruleType, setRuleType] = useState("assertion");
  const [ruleExpression, setRuleExpression] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const { isDarkMode } = useTheme();

  const handleTitleChange = (event) => {
    setRuleTitle(event.target.value);
    setErrorMsg("");
  }

  const handleRuleTypeChange = (event) => {
    setRuleType(event.target.value);
  };

  const handleExpressionChange = (value) => {
    setRuleExpression(value);
    setErrorMsg("");
  }
  const onGenerate = async () => {
    setIsGenerating(true);
    const response = await dispatch(generateRuleWithAI({ ruleText: ruleTitle, testCaseSetId: testCaseSetId, ruleType: ruleType }));
    if (response?.payload && !response?.error) {
      if (response?.payload?.js_expression) {
        setRuleExpression(response.payload.js_expression);
        setIsGenerating(false);
      } else {
        openSnackbar({ message: "Unable to understand the rule text, please write rule expression yourself!", severity: 'error' });
        setIsGenerating(false);
      }
    } else {
      openSnackbar({ message: response?.payload?.error || "Failed to genearate rule expression, please try again!", severity: 'error' });
      setIsGenerating(false);
    }
  }

  const onCancelClick = () => {
    resetState();
    onCancel();
  }

  const resetState = () => {
    setErrorMsg("");
    setRuleExpression("");
    setRuleTitle("");
    setRuleType("assertion");
  }

  const onSaveRule = async () => {
    if (!coreUtils.isStringInvalidOrBlank(ruleTitle) && !coreUtils.isStringInvalidOrBlank(ruleExpression)) {
      const response = await dispatch(createTestRule({ testCaseSetId: testCaseSetId, title: ruleTitle.trim(), expression: ruleExpression.trim(), ruleType: ruleType }));
      if (response?.payload) {
        openSnackbar({ message: "Rule created successfully!", severity: "success" });
      } else {
        openSnackbar({ message: "Rule creation failed!", severity: "error" });
      }
      onSave(response?.payload);
      resetState();
    } else {
      setErrorMsg("Invalid rule title or expression.");
    }
  }

  const renderValue = (selected) => {
    const icon = selected === "assertion" ? <FileCode size={20} /> : <Database size={20} />;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {icon}
      </Box>
    );
  };

  return (
    <Box sx={{ mt: 1 }}>
      <div className="flex items-center gap-2">
        <div className="w-[65px]">
          <FormControl size="small" fullWidth>  
            <Select
              value={ruleType}
              onChange={handleRuleTypeChange}
              onClick={(e) => { e.stopPropagation(); }}
              sx={{
                bgcolor: 'var(--background-secondary)',
                '& .MuiSelect-select': { py: 1.25 }
              }}
              disabled={isGenerating}
              renderValue={renderValue}
            >
              <MenuItem value="assertion" sx={{ px: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FileCode size={16} />
                  <Typography>Assertion</Typography>
                </Box>
              </MenuItem>
              <MenuItem value="db_validation" sx={{ px: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Database size={16} />
                  <Typography>DB Validation</Typography>
                </Box>
              </MenuItem>
            </Select>
          </FormControl>
        </div>
        <div className="flex-1">
          <TextField
            size="small"
            disabled={isGenerating}
            variant="outlined"
            placeholder="Test rule title"
            fullWidth
            name="ruleTitle"
            value={ruleTitle}
            onChange={handleTitleChange}
            sx={{
              bgcolor: 'var(--background-secondary)',
              '& .MuiOutlinedInput-root': { py: 0.25 }
            }}
          />
        </div>
        <div className="whitespace-nowrap">
          <Button
            variant="contained"
            disabled={!ruleTitle || isGenerating}
            onClick={onGenerate}
            sx={{
              fontWeight: 500,
              textTransform: 'none',
              height: 40,
              px: 0.75
            }}
          >
            Generate with AI
            <LoadingOverlay isLoading={isGenerating} />
          </Button>
        </div>
      </div>

      <Box sx={{
        border: "1px solid var(--borderColor)",
        borderRadius: "4px",
        p: 1.5,
        mt: 2,
        bgcolor: isDarkMode ? 'var(--background-primary)' : 'var(--background-secondary)'
      }}>
        <Typography sx={{
          mb: 0.5,
          fontWeight: 600,
          position: "relative",
          color: 'var(--text-primary)',
          fontSize: '0.9rem'
        }}>
          function () {"{"}
          <Tooltip
            title="Function should always return a boolean value."
            placement="left-end"
            sx={{ fontWeight: "550" }}
          >
            <InfoOutlined size="small" sx={{ position: "absolute", right: 0, cursor: "pointer", color: 'var(--text-secondary)' }} />
          </Tooltip>
        </Typography>
        <MonacoEditorWithFullscreen
          className="jsEditor"
          language="javascript"
          theme={isDarkMode ? "js-theme-dark" : "js-theme-light"}
          value={ruleExpression}
          height="180px"
          onChange={handleExpressionChange}
          options={{
            automaticLayout: true,
            wordWrap: "on",
            minimap: { enabled: false },
            lineNumbers: "on",
            tabSize: 2,
            fontSize: 13,
            fontFamily: 'Consolas, Monaco, "Courier New", monospace',
            scrollBeyondLastLine: false,
            scrollbar: {
              vertical: 'visible',
              horizontal: 'visible',
              verticalScrollbarSize: 14,
              horizontalScrollbarSize: 14,
              verticalSliderSize: 14,
              horizontalSliderSize: 14,
              useShadows: false,
              arrowSize: 14,
            },
            overviewRulerBorder: false,
            hideCursorInOverviewRuler: true,
            renderLineHighlight: 'all',
            matchBrackets: 'always',
            autoClosingBrackets: 'always',
            autoClosingQuotes: 'always',
            formatOnPaste: true,
            formatOnType: true,
          }}
        />
        <Typography sx={{ fontWeight: 600, color: 'var(--text-primary)', mt: 0.5 }}>{"}"}</Typography>
      </Box>

      {errorMsg && <Typography color="error" sx={{ mt: 1 }}>{errorMsg}</Typography>}

      <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
        <CustomButton
          startIcon={<Add />}
          variant="outlined"
          disabled={!ruleExpression || !ruleTitle}
          onClick={onSaveRule}
          sx={{
            minWidth: 100,
            textTransform: 'none',
            fontWeight: 600
          }}
        >
          Save
        </CustomButton>
        <CustomButton
          startIcon={<Close />}
          variant="outlined"
          onClick={onCancelClick}
          sx={{
            minWidth: 100,
            textTransform: 'none',
            fontWeight: 600
          }}
        >
          Cancel
        </CustomButton>
      </Box>
    </Box>
  )
}

export default TestRuleCustom;
